import {useState, useRef, useEffect, useCallback} from 'react';
import {CAROUSEL_NEXT, CAROUSEL_PREV} from 'app/components/pages/casino/marketing-blocks/CasinoMarketingUtils';

const DEFAULT_INTERVAL_MS = 5000;

type CarouselDirection = typeof CAROUSEL_NEXT | typeof CAROUSEL_PREV;

export const useCarousel = (itemsCount: number, intervalMs: number = DEFAULT_INTERVAL_MS): {
    activeIndex: number;
    handleNextClick: () => void;
    handlePrevClick: () => void;
} => {
    const [activeIndex, setActiveIndex] = useState(0);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const clearIntervalRef = useCallback((): void => {
        if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const switchTo = useCallback((direction: CarouselDirection): void => {
        if (itemsCount <= 1) {
            return;
        }

        setActiveIndex(prev => {
            if (direction === CAROUSEL_NEXT) {
                return (prev + 1) % itemsCount;
            }

            return prev === 0 ? itemsCount - 1 : prev - 1;
        });

        clearIntervalRef();
        intervalRef.current = setInterval(() => switchTo(CAROUSEL_NEXT), intervalMs);
    }, [
        itemsCount,
        intervalMs,
        clearIntervalRef
    ]);

    const handleNextClick = useCallback((): void => {
        switchTo(CAROUSEL_NEXT);
    }, [switchTo]);

    const handlePrevClick = useCallback((): void => {
        switchTo(CAROUSEL_PREV);
    }, [switchTo]);

    useEffect(() => {
        setActiveIndex(0);
    }, [itemsCount]);

    useEffect(() => {
        if (itemsCount <= 1) {
            clearIntervalRef();
        } else {
            intervalRef.current = setInterval(() => switchTo(CAROUSEL_NEXT), intervalMs);
        }

        return () => {
            clearIntervalRef();
        };
    }, [
        itemsCount,
        intervalMs,
        switchTo,
        clearIntervalRef
    ]);

    return {
        activeIndex,
        handleNextClick,
        handlePrevClick
    };
};
